/**
 * Canonical Hannover Stadtbezirk slugs used by the streams.geo_tag column and
 * the Stadtteil heatmap's SVG.
 *
 * The heatmap looks up districts by slug (lowercase ASCII, hyphenated, no
 * umlauts — `ö`→`oe`, `ü`→`ue`, `ß`→`ss`). User-entered geo_tag values like
 * `"Mitte"`, `"Linden-Limmer"`, or `"Südstadt-Bult"` must normalize back to
 * their canonical slug, otherwise the lookup misses and the district stays
 * unfilled even when data exists for it.
 */

export const STADTTEIL_SLUGS = [
  "mitte",
  "vahrenwald-list",
  "bothfeld-vahrenheide",
  "buchholz-kleefeld",
  "misburg-anderten",
  "kirchrode-bemerode-wuelferode",
  "sudstadt-bult",
  "doehren-wuelfel",
  "ricklingen",
  "linden-limmer",
  "ahlem-badenstedt-davenstedt",
  "herrenhausen-stoecken",
  "nord",
] as const;

export type StadtteilSlug = (typeof STADTTEIL_SLUGS)[number];

export const STADTTEIL_DISPLAY_NAMES: Record<StadtteilSlug, string> = {
  mitte: "Mitte",
  "vahrenwald-list": "Vahrenwald-List",
  "bothfeld-vahrenheide": "Bothfeld-Vahrenheide",
  "buchholz-kleefeld": "Buchholz-Kleefeld",
  "misburg-anderten": "Misburg-Anderten",
  "kirchrode-bemerode-wuelferode": "Kirchrode-Bemerode-Wülferode",
  "sudstadt-bult": "Südstadt-Bult",
  "doehren-wuelfel": "Döhren-Wülfel",
  ricklingen: "Ricklingen",
  "linden-limmer": "Linden-Limmer",
  "ahlem-badenstedt-davenstedt": "Ahlem-Badenstedt-Davenstedt",
  "herrenhausen-stoecken": "Herrenhausen-Stöcken",
  nord: "Nord",
};

const SLUG_SET: Set<string> = new Set(STADTTEIL_SLUGS);

function foldUmlauts(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Map any user-entered geo_tag value to its canonical slug, or null if it
 * doesn't correspond to a known Hannover Stadtbezirk.
 *
 * Handles common variations: display names ("Mitte", "Linden-Limmer"),
 * umlauted variants ("Südstadt-Bult"), and slugs typed with the wrong
 * casing or whitespace.
 */
export function slugifyGeoTag(value: string | null | undefined): StadtteilSlug | null {
  if (!value) return null;
  const folded = foldUmlauts(value.trim());
  if (!folded) return null;

  // Exact slug hit
  if (SLUG_SET.has(folded)) return folded as StadtteilSlug;

  // Common alias variants ("südstadt" / "suedstadt" → "sudstadt-bult", etc.)
  // The fold rule maps ü→ue, but the slug uses bare "u" for Südstadt; rewrite.
  const aliasFolded = folded.replace(/ue/g, "u");
  if (SLUG_SET.has(aliasFolded)) return aliasFolded as StadtteilSlug;

  // No match — caller decides how to fall back.
  return null;
}
